/**
 * Did the render actually deliver the body's content — and if not, what happened?
 *
 * Twig discards content emitted outside a `{% block %}` its layout declares, and does
 * it *silently*: `{% extends %}` succeeds, the layout's chrome renders, the email's own
 * content is gone, no error anywhere. `preflight` catches the causes it can enumerate
 * (layout missing, layout isn't a layout, layout declares no such block) and blocks the
 * save. This module checks the **outcome** instead, so a cause nobody enumerated — a
 * layout that renders its blocks conditionally, a fragment that returns nothing — still
 * surfaces rather than showing a blank layout.
 *
 * The canvas used to answer the question with "does the rendered HTML contain any marker
 * at all". That was wrong in both directions:
 *
 * - **Wrong yes.** `compile()` deliberately emits *no* markers for a `rawPassthrough`
 *   (contained/legacy) document, so every custom-HTML template reported its content
 *   missing on every open, with or without a layout. Same for a detached body, whose
 *   hand-written code has no markers to find.
 * - **Wrong yes.** A marker sits *inside* the block's `{% if %}`, so in Preview and
 *   Source — which render real visibility, unlike Design — content that simply does not
 *   match the sample data looked like content that had been discarded.
 * - **Wrong no.** One surviving marker satisfied the check, so losing *one* of several
 *   layout regions was silent. And a total drop where the layout emits no chrome renders
 *   to the empty string, which the check treated as "nothing to say" — precisely the
 *   blank canvas it existed to explain.
 *
 * So the question is asked as a comparison: which markers did the **body** promise, and
 * which of them came back?
 */
import { BLOCK_ID_ATTRIBUTE, BLOCK_ID_PARAM } from './compile';

/**
 * How a marker got into the body, which decides how much its absence proves.
 *
 * - `comment` — a literal `<!--cvn:id-->` the compiler wrote around a block that cannot
 *   mark itself. Twig copies it verbatim, so if it does not come back the content it
 *   bracketed was discarded or never rendered. **Evidence.**
 * - `param` — a `_cvn_id: 'id'` argument, which the *component* is expected to render as
 *   `data-cvn-id`. True for everything with a declared manifest (`rendersOwnMarker`),
 *   assumed structurally rather than verified against the component's markup. So a
 *   partner component that takes the param and never renders the attribute is
 *   indistinguishable here from one whose content was dropped. **Weaker.**
 */
type MarkerOrigin = 'comment' | 'param';

export interface ExpectedMarker {
  id: string;
  origin: MarkerOrigin;
  /**
   * The marker sits inside an `{% if %}` or `{% for %}`, so the body itself makes no
   * promise about it: not rendering is a legitimate outcome of the sample data, not a
   * dropped block.
   */
  conditional: boolean;
}

/** A start marker written by `blockMarkerStart` — the end marker is redundant here. */
const COMMENT_MARKER = /<!--cvn:([A-Za-z0-9_-]+)-->/g;

/** `_cvn_id: 'b1abcd'` in an `{% include %}` / `{% embed %}` argument hash. */
const PARAM_MARKER = new RegExp(`${BLOCK_ID_PARAM}:\\s*'([A-Za-z0-9_-]+)'`, 'g');

/** `data-cvn-id="b1abcd"` in rendered HTML, quoted either way. */
const RENDERED_ATTRIBUTE = new RegExp(
  `${BLOCK_ID_ATTRIBUTE}\\s*=\\s*["']([A-Za-z0-9_-]+)["']`,
  'g',
);

/**
 * Tags that put what follows them inside a branch, and the tags that close it.
 *
 * Lexical, not a parse: the depth only has to be right *at the marker positions*, and
 * these tags are balanced in any body Twig will render at all — an unbalanced one fails
 * to render and the caller reports the error instead. `{% else %}` / `{% elseif %}` are
 * deliberately absent: they continue a branch rather than opening one.
 */
const BRANCH_TAG = /\{%-?\s*(if|for|endif|endfor)\b/g;

/**
 * Every marker the body promises, in source order, each tagged with how it got there
 * and whether a branch could legitimately swallow it.
 *
 * Read off the **compiled body** rather than walked out of the document, and that is the
 * point: a block whose emit produced nothing (an empty raw-HTML block, an unsupported
 * component) never wrote a marker, so walking the document would promise markers the
 * body never contained and report them as dropped. The body is what was actually asked
 * of Twig.
 *
 * It also means a *detached* body — hand-written code, no document behind it — is judged
 * by its own contents, which is the only thing that could be right there.
 */
export function expectedMarkers(body: string): ExpectedMarker[] {
  const depths = branchDepths(body);
  const found = new Map<string, ExpectedMarker>();

  const collect = (pattern: RegExp, origin: MarkerOrigin) => {
    pattern.lastIndex = 0;
    let match = pattern.exec(body);
    while (match !== null) {
      const id = match[1];
      const conditional = depthAt(depths, match.index) > 0;
      const existing = found.get(id);
      // A comment-bracketed include carries both forms of the same id. Keep the
      // stronger origin, and treat the marker as conditional only if every
      // occurrence of it is — the unconditional one is what the body promises.
      if (!existing) {
        found.set(id, { id, origin, conditional });
      } else {
        found.set(id, {
          id,
          origin: existing.origin === 'comment' ? 'comment' : origin,
          conditional: existing.conditional && conditional,
        });
      }
      match = pattern.exec(body);
    }
  };

  collect(COMMENT_MARKER, 'comment');
  collect(PARAM_MARKER, 'param');

  return [...found.values()];
}

/** Marker ids present in rendered HTML, from either marking mechanism. */
export function renderedMarkerIds(html: string): Set<string> {
  const ids = new Set<string>();
  for (const pattern of [RENDERED_ATTRIBUTE, COMMENT_MARKER]) {
    pattern.lastIndex = 0;
    let match = pattern.exec(html);
    while (match !== null) {
      ids.add(match[1]);
      match = pattern.exec(html);
    }
  }
  return ids;
}

/** `{ index, depth }` after each branch tag, ascending — see `BRANCH_TAG`. */
function branchDepths(body: string): { index: number; depth: number }[] {
  const points: { index: number; depth: number }[] = [];
  let depth = 0;
  BRANCH_TAG.lastIndex = 0;
  let match = BRANCH_TAG.exec(body);
  while (match !== null) {
    depth += match[1].startsWith('end') ? -1 : 1;
    points.push({ index: match.index, depth: Math.max(depth, 0) });
    match = BRANCH_TAG.exec(body);
  }
  return points;
}

function depthAt(
  points: { index: number; depth: number }[],
  index: number,
): number {
  let depth = 0;
  for (const point of points) {
    if (point.index > index) break;
    depth = point.depth;
  }
  return depth;
}

export type ContentDropVerdict =
  /** Everything the body promised came back. */
  | { kind: 'ok' }
  /** Nothing did. `layouted` distinguishes "the layout ate it" from "it vanished". */
  | { kind: 'dropped'; layouted: boolean }
  /** Some of it did. Only ever claimed on `comment`-origin evidence. */
  | { kind: 'partial'; count: number }
  /** The body is entirely conditional and no condition matched this sample data. */
  | { kind: 'empty-by-condition' };

export interface ContentDropInput {
  /** The compiled (or, when detached, hand-written) body handed to Twig. */
  body: string;
  /** What came back. */
  html: string;
  /** False for a document with nothing in it, which cannot have lost anything. */
  hasContent: boolean;
  /** The layout wrapped around the body, if any. */
  layouted: boolean;
  /**
   * For a body with no markers at all, whether its text made it through the layout —
   * `null` when the caller could not or need not check. See `bodyTextSurvived`.
   */
  unmarkedProbe?: boolean | null;
}

/**
 * The one place that decides what the canvas is allowed to claim.
 *
 * Deliberately conservative: every branch that cannot distinguish "dropped" from a
 * benign explanation returns `ok`. A wrong warning on a template that is fine costs more
 * than a missing warning on one that isn't — the wrong one was on screen for every
 * custom-HTML template in the account.
 */
export function diagnoseContentDrop(
  input: ContentDropInput,
): ContentDropVerdict {
  const { body, html, hasContent, layouted, unmarkedProbe = null } = input;
  if (!hasContent) return { kind: 'ok' };

  const expected = expectedMarkers(body);

  /**
   * Nothing in the body identifies itself: a contained (`rawPassthrough`) document,
   * whose markers `compile()` suppresses on purpose, or a hand-written detached body.
   * There is no marker evidence to reason from, so the only available signal is whether
   * the text survived the layout — and with no layout there is nothing that could have
   * taken it.
   */
  if (expected.length === 0) {
    if (layouted && unmarkedProbe === false) return { kind: 'dropped', layouted };
    return { kind: 'ok' };
  }

  const present = renderedMarkerIds(html);
  const promised = expected.filter((marker) => !marker.conditional);

  /**
   * Every block is behind a condition. Absence is then the sample data's answer, not a
   * drop — the informational verdict, which the UI states rather than warns about. (A
   * layout drop *also* lands here when the whole body is conditional; the ambiguity is
   * resolved in favour of the harmless reading.)
   */
  if (promised.length === 0) {
    if (expected.some((marker) => present.has(marker.id))) return { kind: 'ok' };
    return { kind: 'empty-by-condition' };
  }

  const missing = promised.filter((marker) => !present.has(marker.id));
  if (missing.length === 0) return { kind: 'ok' };

  // Nothing the body promised came back, including anything a component was merely
  // *assumed* to mark. Whatever the cause, the email is arriving without its content.
  if (missing.length === promised.length) return { kind: 'dropped', layouted };

  /**
   * A partial drop is claimed only on comment markers — literal text Twig either copies
   * or discards. A missing `param` marker has an innocent explanation (a component that
   * takes `_cvn_id` and never renders it), and reporting that as lost content would
   * recreate the false positive this module replaced.
   */
  const evidenced = missing.filter((marker) => marker.origin === 'comment');
  if (evidenced.length === 0) return { kind: 'ok' };
  return { kind: 'partial', count: evidenced.length };
}

/** Words long enough to be worth looking for in the layouted output. */
const PROBE_WORD = /[\p{L}\p{N}]{5,}/gu;
const PROBE_SAMPLE_SIZE = 5;

/**
 * For an unmarked body: did its text make it into the layouted render?
 *
 * The only body with no markers is one the editor did not author, and the only way to
 * ask about content it cannot identify is to render the body *without* its layout and
 * look for that text in the render *with* it. Layouts wrap content, they do not rewrite
 * it, so surviving text is still a substring of the output.
 *
 * Sampled by the longest distinct words rather than compared wholesale: whitespace,
 * entity encoding and attribute order all differ harmlessly between the two renders.
 * Several samples, and *all* of them must be gone before this reports a loss — one word
 * could legitimately be missing from a conditional branch of the layout.
 *
 * Returns `true` (survived) when the bare render has no text worth sampling: there is
 * then nothing to have lost.
 */
export function bodyTextSurvived(bare: string, layouted: string): boolean {
  const samples = sampleWords(textOf(bare));
  if (samples.length === 0) return true;

  const haystack = textOf(layouted).toLowerCase();
  return samples.some((word) => haystack.includes(word));
}

function textOf(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ');
}

/** The longest distinct words in `text`, lowercased, longest first. */
function sampleWords(text: string): string[] {
  const words = new Set<string>();
  PROBE_WORD.lastIndex = 0;
  let match = PROBE_WORD.exec(text);
  while (match !== null) {
    words.add(match[0].toLowerCase());
    match = PROBE_WORD.exec(text);
  }
  return [...words]
    .sort((left, right) => right.length - left.length)
    .slice(0, PROBE_SAMPLE_SIZE);
}
